import React, { useState, useEffect } from 'react';
import { userRequest } from '../../requestMethods';
import { 
    Box, 
    Button, 
    Typography, 
    Paper, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Pagination,
    CircularProgress,
    Alert
} from '@mui/material';
import DictionaryPhraseEditor from './DictionaryPhraseEditor';
import PageContainer from '../layout/PageContainer';

const PhrasesList = () => {
    const [phrases, setPhrases] = useState([]);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [selectedPhrase, setSelectedPhrase] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Search and pagination states - load from localStorage or use defaults
    const [searchTerm, setSearchTerm] = useState(() => {
        const saved = localStorage.getItem('phrasesList_searchTerm');
        return saved || '';
    });
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(() => {
        const saved = localStorage.getItem('phrasesList_searchTerm');
        return saved || '';
    });
    
    // Pagination states - load from localStorage or use defaults
    const [currentPage, setCurrentPage] = useState(() => {
        const saved = localStorage.getItem('phrasesList_currentPage');
        return saved ? parseInt(saved, 10) : 1;
    });
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem('phrasesList_pageSize');
        return saved ? parseInt(saved, 10) : 10;
    });
    const [totalPages, setTotalPages] = useState(0);
    const [totalPhrases, setTotalPhrases] = useState(0);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            const savedSearchTerm = localStorage.getItem('phrasesList_searchTerm') || '';
            
            // Update debounced search term
            setDebouncedSearchTerm(searchTerm);
            
            // Only reset to page 1 if this is a user-initiated search change
            // (not when component mounts with saved search term)
            if (searchTerm !== savedSearchTerm) {
                setCurrentPage(1);
            }
        }, 500); // 500ms delay

        return () => {
            clearTimeout(handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    useEffect(() => {
        fetchPhrases();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, debouncedSearchTerm]);

    // Save pagination settings to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('phrasesList_currentPage', currentPage.toString());
    }, [currentPage]);

    useEffect(() => {
        localStorage.setItem('phrasesList_pageSize', pageSize.toString());
    }, [pageSize]);

    // Save search term to localStorage
    useEffect(() => {
        localStorage.setItem('phrasesList_searchTerm', searchTerm);
    }, [searchTerm]);

    const fetchPhrases = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: pageSize.toString(),
                ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() })
            });
            
            const response = await userRequest.get(`/dictionaryPhrases?${params}`);
            setPhrases(response.data.phrases);
            setTotalPages(response.data.pagination.totalPages);
            setTotalPhrases(response.data.pagination.totalPhrases);
            
            // If current page is greater than total pages, reset to last page
            if (currentPage > response.data.pagination.totalPages && response.data.pagination.totalPages > 0) {
                setCurrentPage(response.data.pagination.totalPages);
            }
            
            setError('');
        } catch (error) {
            console.error('Error fetching phrases:', error);
            setError('Failed to fetch phrases');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (phrase) => {
        setSelectedPhrase(phrase);
        setIsEditorOpen(true);
    };

    const handleDelete = async (id, phraseName) => {
        if (window.confirm(`Are you sure you want to delete "${phraseName}"?`)) {
            try {
                await userRequest.delete(`/dictionaryPhrases/${id}`);
                
                // If we're on the last page and this is the only item, go to previous page
                if (phrases.length === 1 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                } else {
                    // Refresh current page
                    await fetchPhrases();
                }
            } catch (error) {
                console.error('Error deleting phrase:', error);
                setError('Failed to delete phrase');
            }
        }
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(event.target.value);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const handleEditorClose = () => {
        setIsEditorOpen(false);
        setSelectedPhrase(null);
        fetchPhrases();
    };

    if (loading) {
        return (
            <PageContainer>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Box sx={{ my: 4 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h4" component="h1">
                        Phrases Dictionary
                    </Typography>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => setIsEditorOpen(true)}
                    >
                        Add New Phrase
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <TextField
                    fullWidth
                    label="Search phrases..."
                    variant="outlined"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{ mb: 2 }}
                    placeholder="Search by phrase or display phrase"
                />

                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6">
                        {totalPhrases} {totalPhrases === 1 ? 'phrase' : 'phrases'} found
                    </Typography>
                    
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Items per page</InputLabel>
                        <Select
                            value={pageSize}
                            label="Items per page"
                            onChange={handlePageSizeChange}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Phrase</TableCell>
                                <TableCell>Display Phrase</TableCell>
                                <TableCell>Language</TableCell>
                                <TableCell>Translations</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {phrases.map((phrase) => (
                                <TableRow key={phrase._id}>
                                    <TableCell>{phrase.phrase}</TableCell>
                                    <TableCell>
                                        {phrase.displayPhrase || (
                                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                                Not set
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{phrase.language?.name || 'N/A'}</TableCell>
                                    <TableCell>
                                        {phrase.translations.map((trans, idx) => (
                                            <div key={idx}>
                                                {trans.language?.name}: {trans.value}
                                            </div>
                                        ))}
                                    </TableCell>
                                    <TableCell>
                                        <Button 
                                            onClick={() => handleEdit(phrase)}
                                            color="primary"
                                            size="small"
                                        >
                                            Edit
                                        </Button>
                                        <Button 
                                            onClick={() => handleDelete(phrase._id, phrase.phrase)}
                                            color="error"
                                            size="small"
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {phrases.length === 0 && !loading && (
                    <Box textAlign="center" py={4}>
                        <Typography variant="h6" color="text.secondary">
                            No phrases found
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setIsEditorOpen(true)}
                            sx={{ mt: 2 }}
                        >
                            Create Your First Phrase
                        </Button>
                    </Box>
                )}

                {totalPages > 1 && (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1} sx={{ mt: 3 }}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(event, value) => {
                                setCurrentPage(value);
                            }}
                            color="primary"
                            size="large"
                            showFirstButton
                            showLastButton
                        />
                        <Typography variant="body2" color="text.secondary">
                            Page {currentPage} of {totalPages} 
                            ({Math.min((currentPage - 1) * pageSize + 1, totalPhrases)}-{Math.min(currentPage * pageSize, totalPhrases)} of {totalPhrases} phrases)
                        </Typography>
                    </Box>
                )}
            </Box>

            <DictionaryPhraseEditor 
                open={isEditorOpen}
                onClose={handleEditorClose}
                phrase={selectedPhrase}
            />
        </PageContainer>
    );
};

export default PhrasesList; 